/*
BoxSpirit is a custom component that displays the details of a spirit record in a box format. It is used in the Provenance module to show the details of a spirit record. 

The component receives a record prop and displays the name, producer/distillery, region/country, age statement, ABV, product ID, batch number, SKU, and description of the spirit. The component uses the Box, Typography, and Chip components from the Material-UI library to display the details. 

The Box component is used to create a box layout for the spirit details. The Typography component is used to display the title and description of the spirit. The Chip component is used to display the age statement and ABV of the spirit. 

The component is used in the ProvenanceList component in the Provenance module to display the details of a spirit record. 
*/

import React from 'react';
import { useTheme, Typography, Chip, Box } from '@mui/material';
import { TextInput, SelectInput } from 'react-admin';
import { required } from 'ra-core';

/**
 * Spirit product box component
 * @component
 * @example
 * // Example record structure:
 * const spiritRecord = {
 *   id: "S123",
 *   name: "Highland Reserve",
 *   producer: "Glen Distillery",
 *   region: "Scotland",
 *   ageStatement: "12 Years",
 *   abv: "43.5",
 *   batchNumber: "BN456",
 *   sku: "SKU789",
 *   description: "Complex character with notes of vanilla and oak...",
 *   image: "url/to/spirit/image.jpg"
 * };
 */

export const defaultSpiritImage = "https://placehold.co/200x300?text=Spirit&font=playfair-display";

const BoxSpirit = ({ record }) => {
    const theme = useTheme();
    return (
        <Box sx={{ flex: 1 }}>
            <Typography variant="h6" component="div" sx={{ color: theme.palette.primary.main, mb: 1 }}>
                {record.name || 'Unnamed Spirit'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Producer/Distillery:</strong> {record?.producer || 'Unknown'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Region/Country:</strong> {record?.region || 'Unknown'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Chip label={record?.ageStatement || 'No Age Statement'} size="small" color="primary" variant="outlined" />
                <Chip label={record?.abv ? `${record.abv}% ABV` : 'N/A'} size="small" color="secondary" variant="outlined" />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                <Typography variant="body2"><strong>Product ID:</strong> {record.id || 'N/A'}</Typography>
                <Typography variant="body2"><strong>Batch Number:</strong> {record?.batchNumber || 'N/A'}</Typography>
                <Typography variant="body2"><strong>SKU:</strong> {record?.sku || 'N/A'}</Typography>
            </Box>
            <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    <strong>Description:</strong> {record?.description || 'No description available'}
                </Typography>
            </Box>
        </Box>
    );
};

export const renderSpiritFields = () => {
    return (
        <>
            <TextInput label="Spirit" source="data.name" validate={required()} />
            <TextInput label="Producer/Distillery" source="data.producer" validate={required()} />
            <TextInput label="Region/Country" source="data.region" validate={required()} />
            <TextInput label="Age Statement" source="data.ageStatement" />
            <TextInput label="ABV (%)" source="data.abv" validate={required()} />
            <TextInput label="Batch #" source="data.batchNumber" validate={required()} />
            <TextInput label="SKU" source="data.sku" validate={required()} />
            <TextInput label="Price" source="data.price" validate={required()} />
            <TextInput label="Description" multiline source="data.description" />
        </>
    );
};

export default BoxSpirit;